import { useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { Holding, AccountType, AssetClass } from '@/types/portfolio';

interface DbHolding {
  id: string;
  user_id: string;
  account_id: string | null;
  ticker: string;
  name: string | null;
  shares: number;
  current_price: number;
  cost_basis: number | null;
  account_type: string | null;
  asset_class: string | null;
  expense_ratio: number | null;
  sector: string | null;
  is_manual_entry: boolean;
  last_updated: string;
  created_at: string;
}

export function useHoldings() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Transform database holding to app holding
  const transformHolding = useCallback((dbHolding: DbHolding): Holding => ({
    id: dbHolding.id,
    ticker: dbHolding.ticker,
    name: dbHolding.name || dbHolding.ticker,
    shares: dbHolding.shares,
    currentPrice: dbHolding.current_price,
    costBasis: dbHolding.cost_basis || dbHolding.current_price,
    accountType: (dbHolding.account_type as AccountType) || 'Taxable',
    assetClass: (dbHolding.asset_class as AssetClass) || 'US Stocks',
    expenseRatio: dbHolding.expense_ratio || undefined,
  }), []);

  // Fetch holdings
  const { data: holdings = [], isLoading: loading, error } = useQuery({
    queryKey: ['holdings', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      const { data, error } = await supabase
        .from('holdings')
        .select('*')
        .eq('user_id', user.id)
        .order('ticker', { ascending: true });

      if (error) throw error;
      return (data as DbHolding[]).map(transformHolding);
    },
    enabled: !!user?.id,
  });

  // Add Holding Mutation
  const addHoldingMutation = useMutation({
    mutationFn: async (holding: Omit<Holding, 'id'>) => {
      if (!user?.id) throw new Error('No user');
      const { data, error } = await supabase
        .from('holdings')
        .insert({
          user_id: user.id,
          ticker: holding.ticker,
          name: holding.name,
          shares: holding.shares,
          current_price: holding.currentPrice,
          cost_basis: holding.costBasis,
          account_type: holding.accountType,
          asset_class: holding.assetClass,
          expense_ratio: holding.expenseRatio || null,
          is_manual_entry: true,
        })
        .select()
        .single();

      if (error) throw error;
      return transformHolding(data as DbHolding);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['holdings', user?.id] });
    },
  });

  // Update Holding Mutation
  const updateHoldingMutation = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<Holding> }) => {
      if (!user?.id) throw new Error('No user');
      const dbUpdates: Record<string, unknown> = {};
      if (updates.ticker !== undefined) dbUpdates.ticker = updates.ticker;
      if (updates.name !== undefined) dbUpdates.name = updates.name;
      if (updates.shares !== undefined) dbUpdates.shares = updates.shares;
      if (updates.currentPrice !== undefined) dbUpdates.current_price = updates.currentPrice;
      if (updates.costBasis !== undefined) dbUpdates.cost_basis = updates.costBasis;
      if (updates.accountType !== undefined) dbUpdates.account_type = updates.accountType;
      if (updates.assetClass !== undefined) dbUpdates.asset_class = updates.assetClass;
      if (updates.expenseRatio !== undefined) dbUpdates.expense_ratio = updates.expenseRatio;
      dbUpdates.last_updated = new Date().toISOString();

      const { error } = await supabase
        .from('holdings')
        .update(dbUpdates)
        .eq('id', id)
        .eq('user_id', user.id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['holdings', user?.id] });
    },
  });

  // Delete Holding Mutation
  const deleteHoldingMutation = useMutation({
    mutationFn: async (id: string) => {
      if (!user?.id) throw new Error('No user');
      const { error } = await supabase
        .from('holdings')
        .delete()
        .eq('id', id)
        .eq('user_id', user.id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['holdings', user?.id] });
    },
  });

  // Bulk Upsert Mutation
  const bulkUpsertMutation = useMutation({
    mutationFn: async (newHoldings: Omit<Holding, 'id'>[]) => {
      if (!user?.id) throw new Error('No user');
      const dbHoldings = newHoldings.map(h => ({
        user_id: user.id,
        ticker: h.ticker,
        name: h.name,
        shares: h.shares,
        current_price: h.currentPrice,
        cost_basis: h.costBasis,
        account_type: h.accountType,
        asset_class: h.assetClass,
        expense_ratio: h.expenseRatio || null,
        is_manual_entry: true,
      }));

      const { error } = await supabase
        .from('holdings')
        .insert(dbHoldings);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['holdings', user?.id] });
    },
  });

  // Clear Manual Holdings Mutation
  const clearManualHoldingsMutation = useMutation({
    mutationFn: async () => {
      if (!user?.id) throw new Error('No user');
      const { error } = await supabase
        .from('holdings')
        .delete()
        .eq('user_id', user.id)
        .eq('is_manual_entry', true);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['holdings', user?.id] });
    },
  });

  // Wrapper functions to maintain API compatibility
  const addHolding = async (holding: Omit<Holding, 'id'>) => {
    try {
      return await addHoldingMutation.mutateAsync(holding);
    } catch (e) {
      console.error(e);
      return null;
    }
  };

  const updateHolding = async (id: string, updates: Partial<Holding>) => {
    try {
      await updateHoldingMutation.mutateAsync({ id, updates });
      return true;
    } catch (e) {
      console.error(e);
      return false;
    }
  };

  const deleteHolding = async (id: string) => {
    try {
      await deleteHoldingMutation.mutateAsync(id);
      return true;
    } catch (e) {
      console.error(e);
      return false;
    }
  };

  const bulkUpsertHoldings = async (newHoldings: Omit<Holding, 'id'>[]) => {
    try {
      await bulkUpsertMutation.mutateAsync(newHoldings);
      return true;
    } catch (e) {
      console.error(e);
      return false;
    }
  };

  const clearManualHoldings = async () => {
    try {
      await clearManualHoldingsMutation.mutateAsync();
      return true;
    } catch (e) {
      console.error(e);
      return false;
    }
  };

  return {
    holdings,
    loading,
    error: error ? (error as Error).message : null,
    fetchHoldings: () => queryClient.invalidateQueries({ queryKey: ['holdings', user?.id] }),
    addHolding,
    updateHolding,
    deleteHolding,
    bulkUpsertHoldings,
    clearManualHoldings,
    // No-op setter for compatibility
    setHoldings: () => {},
  };
}
